import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  ButtonGroup,
  Spinner,
  Select,
  TextField,
  Box,
  Badge,
  Banner,
  Divider,
  ProgressBar,
  Icon,
  Tooltip,
  Collapsible,
  EmptyState,
  Thumbnail,
  Link,
  InlineGrid,
  SkeletonBodyText,
  SkeletonDisplayText,
} from "@shopify/polaris";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  RefreshIcon,
  EditIcon,
  ViewIcon,
  ImageIcon,
  TextIcon,
  CalendarIcon,
  SearchIcon,
  LinkIcon,
  NoteIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@shopify/polaris-icons";
import { notifyReviewValue } from "./ReviewManager";

/* -------------------------------------------------------------------------- */
/* API                                                                        */
/* -------------------------------------------------------------------------- */

async function auditRequest(method, shop) {
  if (!window.shopify?.idToken) {
    throw new Error("Please open AnalyticsHero from Shopify Admin.");
  }
  const token = await window.shopify.idToken();
  return axios({
    method,
    url: "/analytics/blog-audit/",
    params: { shop },
    headers: { Authorization: `Bearer ${token}` },
  });
}

/* -------------------------------------------------------------------------- */
/* Config                                                                     */
/* -------------------------------------------------------------------------- */

const PLACEHOLDER_IMAGE =
  "https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png";

const CHECK_META = {
  missing_alt_text: {
    icon: ImageIcon,
    iconTone: "caution",
    badgeTone: "warning",
    short: "Alt text",
  },
  weak_headings: {
    icon: TextIcon,
    iconTone: "warning",
    badgeTone: "attention",
    short: "Headings",
  },
  outdated: {
    icon: CalendarIcon,
    iconTone: "info",
    badgeTone: "info",
    short: "Outdated",
  },
};

const DEFAULT_META = {
  icon: AlertCircleIcon,
  iconTone: "caution",
  badgeTone: "attention",
  short: "Issue",
};

const SORT_OPTIONS = [
  { label: "Most issues first", value: "issues" },
  { label: "Title (A–Z)", value: "title" },
  { label: "Longest first", value: "words_desc" },
  { label: "Shortest first", value: "words_asc" },
  { label: "Least recently updated", value: "oldest" },
];

const PAGE_SIZE = 5;

const STYLES = `
.bha-clickable { cursor: pointer; transition: box-shadow .15s ease, transform .15s ease; border-radius: 12px; }
.bha-clickable:hover { box-shadow: 0 2px 8px rgba(0,0,0,.12); }
.bha-clickable:focus-visible { outline: 2px solid var(--p-color-border-focus, #005bd3); outline-offset: 2px; }
.bha-check { transition: box-shadow .2s ease; border-radius: 12px; }
.bha-check[data-open="true"] { box-shadow: 0 4px 16px rgba(0,0,0,.10); }
.bha-header { cursor: pointer; }
@media (prefers-reduced-motion: reduce) {
  .bha-clickable, .bha-check { transition: none; }
}
`;

function metaFor(key, label = "") {
  if (CHECK_META[key]) return CHECK_META[key];
  const text = `${key} ${label}`.toLowerCase();
  if (/alt/.test(text)) return CHECK_META.missing_alt_text;
  if (/heading/.test(text)) return CHECK_META.weak_headings;
  if (/outdated|updated|stale|old/.test(text)) return CHECK_META.outdated;
  if (/meta/.test(text))
    return { icon: SearchIcon, iconTone: "caution", badgeTone: "warning", short: "Meta description" };
  if (/product|link/.test(text))
    return { icon: LinkIcon, iconTone: "caution", badgeTone: "warning", short: "Product links" };
  if (/word|short|length/.test(text))
    return { icon: NoteIcon, iconTone: "caution", badgeTone: "attention", short: "Word count" };
  return DEFAULT_META;
}

function formatDate(value, options) {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, options);
}

function getHealth(score) {
  if (score >= 80) return { label: "Healthy", tone: "success", bar: "success" };
  if (score >= 50) return { label: "Needs attention", tone: "caution", bar: "highlight" };
  return { label: "Needs work", tone: "critical", bar: "critical" };
}

function onActivate(handler) {
  return (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handler();
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Small building blocks                                                      */
/* -------------------------------------------------------------------------- */

function StatTile({ label, value, tone, helper, onClick, active, hint }) {
  const content = (
    <Box
      padding="400"
      background={active ? "bg-surface-selected" : "bg-surface"}
      borderWidth={active ? "050" : "025"}
      borderColor={active ? "border-emphasis" : "border"}
      borderRadius="300"
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="heading2xl" tone={tone}>
          {value}
        </Text>
        {(helper || hint) && (
          <Text as="p" variant="bodySm" tone="subdued">
            {helper}
            {helper && hint ? " · " : ""}
            {hint}
          </Text>
        )}
      </BlockStack>
    </Box>
  );

  if (!onClick) return content;

  return (
    <div
      className="bha-clickable"
      role="button"
      tabIndex={0}
      aria-pressed={!!active}
      onClick={onClick}
      onKeyDown={onActivate(onClick)}
    >
      {content}
    </div>
  );
}

function IssueDetail({ post, issueKey }) {
  if (issueKey === "missing_alt_text") {
    const count = post.missing_inline_alt_count || 0;
    return (
      <BlockStack gap="100">
        <Text as="p" fontWeight="medium">
          Missing alt text
        </Text>
        <Text as="p" tone="subdued">
          {count} inline {count === 1 ? "image needs" : "images need"} alt text
          {post.missing_featured_alt ? " and the featured image needs it too" : ""}.
        </Text>
      </BlockStack>
    );
  }

  if (issueKey === "weak_headings") {
    const reasons = post.heading_reasons || [];
    return (
      <BlockStack gap="100">
        <Text as="p" fontWeight="medium">
          Heading structure
        </Text>
        {reasons.length > 0 ? (
          <BlockStack gap="050">
            {reasons.map((reason) => (
              <InlineStack key={reason} gap="200" wrap={false} blockAlign="start">
                <Box paddingBlockStart="100">
                  <Icon source={AlertCircleIcon} tone="caution" />
                </Box>
                <Text as="p" tone="subdued">
                  {reason}
                </Text>
              </InlineStack>
            ))}
          </BlockStack>
        ) : (
          <Text as="p" tone="subdued">
            The heading structure could be improved.
          </Text>
        )}
      </BlockStack>
    );
  }

  if (issueKey === "outdated" && post.updated_at) {
    return (
      <BlockStack gap="100">
        <Text as="p" fontWeight="medium">
          Last updated
        </Text>
        <Text as="p" tone="subdued">
          {formatDate(post.updated_at, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Text>
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="100">
      <Text as="p" fontWeight="medium">
        Flagged by this check
      </Text>
      <Text as="p" tone="subdued">
        {issueKey.includes("word") || issueKey.includes("short")
          ? `This post has ${post.word_count} words. Aim for at least 300.`
          : "Open the post in Shopify and apply the recommendation above."}
      </Text>
    </BlockStack>
  );
}

function PostRow({ post, issueKey, labels, expanded, onToggle, reviewed, onToggleReviewed }) {
  return (
    <Box
      padding="400"
      background={reviewed ? "bg-surface-success" : "bg-surface"}
      borderWidth="025"
      borderColor={expanded ? "border-emphasis" : "border"}
      borderRadius="300"
    >
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Thumbnail
              source={post.featured_image_url || PLACEHOLDER_IMAGE}
              alt={post.title}
              size="medium"
            />
            <BlockStack gap="100">
              <Text as="h4" variant="headingSm" breakWord>
                {post.title}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {post.blog_title} · {post.word_count} words
              </Text>
              <InlineStack gap="100">
                {reviewed && (
                  <Badge tone="success" size="small" icon={CheckCircleIcon}>
                    Reviewed
                  </Badge>
                )}
                {(post.issues || []).map((key) => (
                  <Badge key={key} tone={metaFor(key, labels[key]).badgeTone} size="small">
                    {labels[key] || metaFor(key).short}
                  </Badge>
                ))}
              </InlineStack>
            </BlockStack>
          </InlineStack>

          <ButtonGroup>
            <Tooltip content={reviewed ? "Mark as not reviewed" : "Mark as reviewed"}>
              <Button
                icon={CheckCircleIcon}
                pressed={reviewed}
                onClick={onToggleReviewed}
                accessibilityLabel={reviewed ? "Mark as not reviewed" : "Mark as reviewed"}
              />
            </Tooltip>
            <Tooltip content="Edit in Shopify">
              <Button
                url={post.edit_url}
                external
                icon={EditIcon}
                accessibilityLabel={`Edit ${post.title} in Shopify`}
              />
            </Tooltip>
            <Button
              onClick={onToggle}
              icon={expanded ? ChevronUpIcon : ChevronDownIcon}
              ariaExpanded={expanded}
              ariaControls={`post-details-${post.id}`}
            >
              {expanded ? "Hide" : "Details"}
            </Button>
          </ButtonGroup>
        </InlineStack>

        <Collapsible
          open={expanded}
          id={`post-details-${post.id}`}
          transition={{ duration: "200ms", timingFunction: "ease-in-out" }}
        >
          <BlockStack gap="300">
            <Divider />
            <Box padding="300" background="bg-surface-secondary" borderRadius="200">
              <IssueDetail post={post} issueKey={issueKey} />
            </Box>
            <InlineStack gap="200">
              <Button url={post.edit_url} external icon={EditIcon} size="slim">
                Edit in Shopify
              </Button>
              <Button
                url={post.storefront_url}
                external
                icon={ViewIcon}
                variant="tertiary"
                size="slim"
              >
                View post
              </Button>
            </InlineStack>
          </BlockStack>
        </Collapsible>
      </BlockStack>
    </Box>
  );
}

/* Panel that opens directly under the selected check */
function AffectedPostsPanel({
  definition,
  checkPostCount,
  reviewedCount,
  posts,
  visiblePosts,
  remaining,
  query,
  onQueryChange,
  sort,
  onSortChange,
  labels,
  issueKey,
  expandedPost,
  onToggleExpanded,
  reviewed,
  onToggleReviewed,
  onShowMore,
  showDivider = true,
  title = "Affected posts",
  onClose,
}) {
  const reviewedPercent =
    checkPostCount > 0 ? Math.round((reviewedCount / checkPostCount) * 100) : 0;

  return (
    <BlockStack gap="400">
      {showDivider && <Divider />}

      <InlineStack align="space-between" blockAlign="center" gap="300">
        <BlockStack gap="050">
          <Text as="h4" variant="headingSm">
            {title}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {reviewedCount} of {checkPostCount} reviewed this session
          </Text>
        </BlockStack>
        <InlineStack gap="300" blockAlign="center">
          <div style={{ width: 140 }}>
            <ProgressBar progress={reviewedPercent} size="small" tone="success" animated={false} />
          </div>
          {onClose && (
            <Button variant="tertiary" onClick={onClose}>
              Close
            </Button>
          )}
        </InlineStack>
      </InlineStack>

      {definition.recommendation && (
        <Banner tone="info" title="How to fix it">
          <Text as="p">{definition.recommendation}</Text>
        </Banner>
      )}

      {checkPostCount > 1 && (
        <InlineGrid columns={{ xs: 1, sm: "2fr 1fr" }} gap="300">
          <TextField
            label="Search posts"
            labelHidden
            placeholder="Search by post or blog title"
            value={query}
            onChange={onQueryChange}
            prefix={<Icon source={SearchIcon} />}
            clearButton
            onClearButtonClick={() => onQueryChange("")}
            autoComplete="off"
          />
          <Select
            label="Sort posts"
            labelHidden
            options={SORT_OPTIONS}
            value={sort}
            onChange={onSortChange}
          />
        </InlineGrid>
      )}

      {posts.length === 0 ? (
        <Box padding="500" background="bg-surface-secondary" borderRadius="300">
          <BlockStack gap="200" inlineAlign="center">
            <Text as="p" fontWeight="medium">
              No posts match
            </Text>
            <Text as="p" tone="subdued">
              Try a different search term.
            </Text>
            {query && <Button onClick={() => onQueryChange("")}>Clear search</Button>}
          </BlockStack>
        </Box>
      ) : (
        <BlockStack gap="300">
          {visiblePosts.map((post) => (
            <PostRow
              key={post.id}
              post={post}
              issueKey={issueKey}
              labels={labels}
              expanded={expandedPost === post.id}
              onToggle={() => onToggleExpanded(post.id)}
              reviewed={!!reviewed[`${issueKey}:${post.id}`]}
              onToggleReviewed={() => onToggleReviewed(`${issueKey}:${post.id}`)}
            />
          ))}

          {remaining > 0 && (
            <InlineStack align="center">
              <Button onClick={onShowMore}>
                {`Show ${Math.min(remaining, PAGE_SIZE)} more (${remaining} remaining)`}
              </Button>
            </InlineStack>
          )}
        </BlockStack>
      )}
    </BlockStack>
  );
}

function getColumnCount() {
  if (typeof window === "undefined") return 3;
  if (window.innerWidth < 700) return 1;
  if (window.innerWidth < 1100) return 2;
  return 3;
}

function useColumnCount() {
  const [count, setCount] = useState(getColumnCount);
  useEffect(() => {
    const onResize = () => setCount(getColumnCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return count;
}

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/* Compact card used in "Columns" layout */
function CheckCard({ check, total, selected, onToggle }) {
  const meta = metaFor(check.key, check.label);
  const passed = check.count === 0;
  const percent = total > 0 ? Math.round((check.count / total) * 100) : 0;

  return (
    <div
      className={passed ? "bha-check" : "bha-check bha-clickable"}
      data-open={selected}
      style={{ height: "100%" }}
      onClick={passed ? undefined : onToggle}
    >
      <Box
        padding="400"
        background={selected ? "bg-surface-selected" : "bg-surface"}
        borderWidth={selected ? "050" : "025"}
        borderColor={selected ? "border-emphasis" : "border"}
        borderRadius="300"
        minHeight="100%"
      >
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="start" wrap={false}>
            <Box
              padding="200"
              background={passed ? "bg-surface-success" : "bg-surface-secondary"}
              borderRadius="200"
            >
              <Icon
                source={passed ? CheckCircleIcon : meta.icon}
                tone={passed ? "success" : meta.iconTone}
              />
            </Box>
            {passed ? (
              <Badge tone="success" icon={CheckCircleIcon}>
                Passed
              </Badge>
            ) : (
              <Badge tone={meta.badgeTone}>
                {`${check.count} ${check.count === 1 ? "post" : "posts"}`}
              </Badge>
            )}
          </InlineStack>

          <BlockStack gap="100">
            <Text as="p" fontWeight="semibold">
              {check.label}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {`${check.count} of ${total} ${check.label.toLowerCase()}`}
            </Text>
          </BlockStack>

          <ProgressBar
            progress={percent}
            size="small"
            tone={passed ? "success" : "highlight"}
            animated={false}
          />

          {passed ? (
            <Text as="p" variant="bodySm" tone="subdued">
              No action needed
            </Text>
          ) : (
            <Button
              fullWidth
              variant={selected ? "primary" : "secondary"}
              disclosure={selected ? "up" : "down"}
              ariaExpanded={selected}
            >
              {selected ? "Hide posts" : "View posts"}
            </Button>
          )}
        </BlockStack>
      </Box>
    </div>
  );
}

/* Full-width panel shown under the row of cards that contains the selected check */
function GridPanel({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(
      () => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      120
    );
    return () => clearTimeout(t);
  }, []);
  return (
    <div ref={ref}>
      <Box
        padding="400"
        background="bg-surface"
        borderWidth="050"
        borderColor="border-emphasis"
        borderRadius="300"
      >
        {children}
      </Box>
    </div>
  );
}

/* One check = header row + collapsible panel (opens right below this row) */
function CheckRow({ check, total, selected, onToggle, children }) {
  const meta = metaFor(check.key, check.label);
  const passed = check.count === 0;
  const percent = total > 0 ? Math.round((check.count / total) * 100) : 0;
  const ref = useRef(null);

  useEffect(() => {
    if (!selected) return undefined;
    const t = setTimeout(
      () => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      180
    );
    return () => clearTimeout(t);
  }, [selected]);

  return (
    <div ref={ref} className="bha-check" data-open={selected}>
      <Box
        padding="400"
        background={selected ? "bg-surface-selected" : "bg-surface"}
        borderWidth={selected ? "050" : "025"}
        borderColor={selected ? "border-emphasis" : "border"}
        borderRadius="300"
      >
        <BlockStack gap="400">
          {/* Clicking anywhere on the header toggles the panel. The button only
              carries semantics; its click bubbles up to this handler. */}
          <div
            className={passed ? undefined : "bha-header"}
            onClick={passed ? undefined : onToggle}
          >
            <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
              <InlineStack gap="300" blockAlign="center" wrap={false}>
                <Box
                  padding="200"
                  background={passed ? "bg-surface-success" : "bg-surface-secondary"}
                  borderRadius="200"
                >
                  <Icon
                    source={passed ? CheckCircleIcon : meta.icon}
                    tone={passed ? "success" : meta.iconTone}
                  />
                </Box>
                <BlockStack gap="050">
                  <Text as="p" fontWeight="semibold">
                    {check.label}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {`${check.count} of ${total} ${check.label.toLowerCase()}`}
                  </Text>
                </BlockStack>
              </InlineStack>

              <InlineStack gap="400" blockAlign="center" wrap={false}>
                <div style={{ width: 120 }}>
                  <ProgressBar
                    progress={percent}
                    size="small"
                    tone={passed ? "success" : "highlight"}
                    animated={false}
                  />
                </div>
                {passed ? (
                  <Badge tone="success" icon={CheckCircleIcon}>
                    Passed
                  </Badge>
                ) : (
                  <>
                    <Badge tone={meta.badgeTone}>
                      {`${check.count} ${check.count === 1 ? "post" : "posts"}`}
                    </Badge>
                    <Button
                      variant={selected ? "primary" : "secondary"}
                      disclosure={selected ? "up" : "down"}
                      ariaExpanded={selected}
                      ariaControls={`check-panel-${check.key}`}
                    >
                      {selected ? "Hide posts" : "View posts"}
                    </Button>
                  </>
                )}
              </InlineStack>
            </InlineStack>
          </div>

          {!passed && (
            <Collapsible
              open={selected}
              id={`check-panel-${check.key}`}
              transition={{ duration: "250ms", timingFunction: "ease-in-out" }}
            >
              {selected ? children : null}
            </Collapsible>
          )}
        </BlockStack>
      </Box>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

export default function BlogAuditCard({ shopOrigin }) {
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");
  const [selectedCheck, setSelectedCheck] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedPost, setExpandedPost] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("issues");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewed, setReviewed] = useState({});
  const [layout, setLayoutState] = useState(() => {
    try {
      return localStorage.getItem("bha-audit-layout") === "columns" ? "columns" : "rows";
    } catch {
      return "rows";
    }
  });
  const columnCount = useColumnCount();

  function setLayout(value) {
    setLayoutState(value);
    try {
      localStorage.setItem("bha-audit-layout", value);
    } catch {
      /* storage unavailable, ignore */
    }
  }

  useEffect(() => {
    if (!shopOrigin) return;
    let cancelled = false;
    let timer;
    setAudit(null);
    setError("");

    async function load() {
      try {
        let response = await auditRequest("get", shopOrigin);
        if (cancelled) return;
        if (response.data.status === "not_started") {
          await auditRequest("post", shopOrigin);
          if (cancelled) return;
          response = await auditRequest("get", shopOrigin);
        }
        if (cancelled) return;
        setAudit(response.data);
        setError("");
        if (["pending", "running"].includes(response.data.status)) {
          timer = setTimeout(load, 4000);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
            err.message ||
            "Unable to load the blog audit."
          );
        }
      }
    }
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shopOrigin, reloadKey]);

  async function restart() {
    setBusy(true);
    setError("");
    try {
      await auditRequest("post", shopOrigin);
      setSelectedCheck("");
      setExpandedPost(null);
      setQuery("");
      setReviewed({});
      setVisibleCount(PAGE_SIZE);
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Unable to start the audit."
      );
    } finally {
      setBusy(false);
    }
  }

  function selectCheck(key) {
    setSelectedCheck(key);
    setExpandedPost(null);
    setQuery("");
    setVisibleCount(PAGE_SIZE);

    if (key && audit?.status === "complete") {
      notifyReviewValue(shopOrigin, "audit_recommendation_opened", {
        check: key,
      });
    }
  }

  function toggleReviewed(key) {
    setReviewed((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  /* ----------------------------- Derived data ----------------------------- */

  const running = audit && ["pending", "running"].includes(audit.status);
  const complete = audit?.status === "complete";
  const scanned = audit?.scanned_count || 0;

  const findings = useMemo(
    () => (audit?.checks || []).filter((check) => check.count > 0),
    [audit]
  );
  const allChecks = useMemo(
    () =>
      [...(audit?.checks || [])].sort(
        (a, b) => Number(a.count === 0) - Number(b.count === 0)
      ),
    [audit]
  );
  const passedChecks = allChecks.filter((c) => c.count === 0).length;
  const visibleChecks = allChecks.filter((c) =>
    statusFilter === "issues"
      ? c.count > 0
      : statusFilter === "passed"
        ? c.count === 0
        : true
  );
  const totalIssues = findings.reduce((sum, check) => sum + check.count, 0);

  const labels = useMemo(
    () => Object.fromEntries((audit?.checks || []).map((c) => [c.key, c.label])),
    [audit]
  );

  const postsWithIssues = useMemo(
    () => (audit?.posts || []).filter((post) => (post.issues || []).length > 0),
    [audit]
  );

  const healthScore =
    scanned > 0
      ? Math.max(0, Math.round(((scanned - postsWithIssues.length) / scanned) * 100))
      : 100;
  const health = getHealth(healthScore);

  const selectedDefinition = findings.find((c) => c.key === selectedCheck);

  const checkPosts = useMemo(
    () => (audit?.posts || []).filter((post) => post.issues?.includes(selectedCheck)),
    [audit, selectedCheck]
  );
  const reviewedCount = checkPosts.filter(
    (post) => reviewed[`${selectedCheck}:${post.id}`]
  ).length;

  const affectedPosts = useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = checkPosts.filter(
      (post) =>
        !term ||
        post.title?.toLowerCase().includes(term) ||
        post.blog_title?.toLowerCase().includes(term)
    );

    const sorted = [...list];
    switch (sort) {
      case "title":
        sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
      case "words_desc":
        sorted.sort((a, b) => (b.word_count || 0) - (a.word_count || 0));
        break;
      case "words_asc":
        sorted.sort((a, b) => (a.word_count || 0) - (b.word_count || 0));
        break;
      case "oldest":
        sorted.sort(
          (a, b) =>
            new Date(a.updated_at || 0).getTime() -
            new Date(b.updated_at || 0).getTime()
        );
        break;
      default:
        sorted.sort((a, b) => b.issues.length - a.issues.length);
    }
    return sorted;
  }, [checkPosts, query, sort]);

  const visiblePosts = affectedPosts.slice(0, visibleCount);
  const remaining = affectedPosts.length - visiblePosts.length;

  /* -------------------------------- Render -------------------------------- */

  return (
    <Box paddingBlockEnd="400">
      <style>{STYLES}</style>
      <Card padding="0">
        {/* Header */}
        <Box padding="500">
          <InlineStack align="space-between" blockAlign="start" gap="400">
            <BlockStack gap="100">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h2" variant="headingLg">
                  Blog content audit
                </Text>
                {complete && totalIssues === 0 && (
                  <Badge tone="success" icon={CheckCircleIcon}>
                    All clear
                  </Badge>
                )}
                {complete && totalIssues > 0 && (
                  <Badge tone="attention">
                    {`${totalIssues} ${totalIssues === 1 ? "issue" : "issues"}`}
                  </Badge>
                )}
                {running && (
                  <Badge tone="info" progress="partiallyComplete">
                    Scanning
                  </Badge>
                )}
              </InlineStack>
              <Text as="p" tone="subdued">
                Actionable checks for your published posts. Results are
                separate from visitor analytics.
              </Text>

              <Banner tone="info">
                <Text as="p">
                  These recommendations are based on your existing blog content.
                  AnalyticsHero is now collecting visitor data, and we'll notify you
                  when your first performance insights are ready.
                </Text>
              </Banner>
            </BlockStack>

            {complete && (
              <Button onClick={restart} loading={busy} icon={RefreshIcon}>
                Re-run audit
              </Button>
            )}
          </InlineStack>
        </Box>

        <Divider />

        <Box padding="500">
          <BlockStack gap="500">
            {/* Error */}
            {error && (
              <Banner
                tone="critical"
                title="Unable to load audit"
                action={{
                  content: "Try again",
                  onAction: () => setReloadKey((value) => value + 1),
                }}
              >
                <Text as="p">{error}</Text>
              </Banner>
            )}

            {/* Initial loading skeleton */}
            {!audit && !error && (
              <BlockStack gap="400">
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" accessibilityLabel="Loading audit" />
                  <Text as="p" tone="subdued">
                    Loading your content audit…
                  </Text>
                </InlineStack>
                <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      padding="400"
                      borderWidth="025"
                      borderColor="border"
                      borderRadius="300"
                    >
                      <BlockStack gap="300">
                        <SkeletonBodyText lines={1} />
                        <SkeletonDisplayText size="large" />
                      </BlockStack>
                    </Box>
                  ))}
                </InlineGrid>
              </BlockStack>
            )}

            {/* Running */}
            {running && (
              <Box padding="500" background="bg-surface-secondary" borderRadius="300">
                <BlockStack gap="300" inlineAlign="center">
                  <Spinner size="large" accessibilityLabel="Auditing posts" />
                  <Text as="p" variant="headingMd" alignment="center">
                    Checking your posts…
                  </Text>
                  <Text as="p" tone="subdued" alignment="center">
                    {audit.scanned_count}{" "}
                    {audit.scanned_count === 1 ? "post" : "posts"} checked so
                    far. You can leave this page. The audit keeps running.
                  </Text>
                </BlockStack>
              </Box>
            )}

            {/* Failed */}
            {audit?.status === "failed" && (
              <Banner
                tone="critical"
                title={audit.error || "The audit could not finish"}
                action={{
                  content: "Retry audit",
                  onAction: restart,
                  loading: busy,
                }}
              >
                <Text as="p">Findings below cover only the posts checked so far.</Text>
              </Banner>
            )}

            {/* Summary */}
            {(complete || audit?.status === "failed") && scanned > 0 && (
              <BlockStack gap="400">
                <Box padding="400" background="bg-surface-secondary" borderRadius="300">
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="end">
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">
                          Content health
                        </Text>
                        <InlineStack gap="200" blockAlign="baseline">
                          <Text as="p" variant="heading3xl" tone={health.tone}>
                            {`${healthScore}%`}
                          </Text>
                          <Text as="p" tone="subdued">
                            {health.label}
                          </Text>
                        </InlineStack>
                      </BlockStack>
                      <Text as="p" variant="bodySm" tone="subdued" alignment="end">
                        {scanned - postsWithIssues.length} of {scanned} posts
                        have no issues
                      </Text>
                    </InlineStack>
                    <ProgressBar
                      progress={healthScore}
                      tone={health.bar}
                      size="medium"
                      animated={false}
                    />
                  </BlockStack>
                </Box>

                <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
                  <StatTile
                    label="Posts scanned"
                    value={scanned}
                    onClick={() => setStatusFilter("all")}
                    active={statusFilter === "all"}
                    hint="Show all checks"
                  />
                  <StatTile
                    label="Issues found"
                    value={totalIssues}
                    tone={totalIssues > 0 ? "caution" : "success"}
                    helper={
                      postsWithIssues.length > 0
                        ? `Across ${postsWithIssues.length} ${postsWithIssues.length === 1 ? "post" : "posts"
                        }`
                        : undefined
                    }
                    onClick={() => setStatusFilter("issues")}
                    active={statusFilter === "issues"}
                    hint="Filter"
                  />
                  <StatTile
                    label="Checks passed"
                    value={passedChecks}
                    tone="success"
                    helper={`of ${(audit.checks || []).length} checks`}
                    onClick={() => setStatusFilter("passed")}
                    active={statusFilter === "passed"}
                    hint="Filter"
                  />
                </InlineGrid>
              </BlockStack>
            )}

            {/* Empty: no posts */}
            {complete && scanned === 0 && (
              <EmptyState heading="No published blog posts found" image={PLACEHOLDER_IMAGE}>
                <Text as="p">
                  Publish a blog post, then run the audit again to see
                  recommendations.
                </Text>
              </EmptyState>
            )}

            {/* All clear */}
            {complete && scanned > 0 && findings.length === 0 && (
              <Banner tone="success" icon={CheckCircleIcon}>
                <Text as="p" fontWeight="medium">
                  No issues found by these basic checks.
                </Text>
                <Text as="p" tone="subdued">
                  Your content is looking healthy. Run the audit again after
                  publishing new posts.
                </Text>
              </Banner>
            )}

            {/* All checks - each row opens its own panel directly below it */}
            {allChecks.length > 0 && scanned > 0 && (
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" gap="300">
                  <BlockStack gap="050">
                    <Text as="h3" variant="headingMd">
                      All checks
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {findings.length > 0
                        ? "Select a check to see the posts that need attention"
                        : "Every check passed"}
                    </Text>
                  </BlockStack>
                  <InlineStack gap="300" blockAlign="center">
                    <ButtonGroup variant="segmented">
                      <Button
                        size="slim"
                        pressed={layout === "rows"}
                        onClick={() => setLayout("rows")}
                      >
                        Rows
                      </Button>
                      <Button
                        size="slim"
                        pressed={layout === "columns"}
                        onClick={() => setLayout("columns")}
                      >
                        Columns
                      </Button>
                    </ButtonGroup>
                    <ButtonGroup variant="segmented">
                      {[
                        ["all", `All (${allChecks.length})`],
                        ["issues", `Issues (${findings.length})`],
                        ["passed", `Passed (${passedChecks})`],
                      ].map(([value, label]) => (
                        <Button
                          key={value}
                          size="slim"
                          pressed={statusFilter === value}
                          onClick={() => setStatusFilter(value)}
                        >
                          {label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </InlineStack>
                </InlineStack>

                <BlockStack gap={layout === "columns" ? "400" : "300"}>
                  {chunk(visibleChecks, layout === "columns" ? columnCount : 1).map(
                    (group, groupIndex) => {
                      const selectedInGroup = group.some(
                        (c) => c.key === selectedCheck && c.count > 0
                      );
                      const renderPanel = (extra) => (
                        <AffectedPostsPanel
                          definition={selectedDefinition}
                          checkPostCount={checkPosts.length}
                          reviewedCount={reviewedCount}
                          posts={affectedPosts}
                          visiblePosts={visiblePosts}
                          remaining={remaining}
                          query={query}
                          onQueryChange={(value) => {
                            setQuery(value);
                            setVisibleCount(PAGE_SIZE);
                          }}
                          sort={sort}
                          onSortChange={setSort}
                          labels={labels}
                          issueKey={selectedCheck}
                          expandedPost={expandedPost}
                          onToggleExpanded={(id) =>
                            setExpandedPost(expandedPost === id ? null : id)
                          }
                          reviewed={reviewed}
                          onToggleReviewed={toggleReviewed}
                          onShowMore={() =>
                            setVisibleCount((count) => count + PAGE_SIZE)
                          }
                          {...extra}
                        />
                      );

                      if (layout === "columns") {
                        return (
                          <BlockStack key={groupIndex} gap="400">
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                                gap: 16,
                              }}
                            >
                              {group.map((check) => (
                                <CheckCard
                                  key={check.key}
                                  check={check}
                                  total={scanned}
                                  selected={selectedCheck === check.key}
                                  onToggle={() =>
                                    selectCheck(
                                      selectedCheck === check.key ? "" : check.key
                                    )
                                  }
                                />
                              ))}
                            </div>
                            {selectedInGroup && selectedDefinition && (
                              <GridPanel key={selectedCheck}>
                                {renderPanel({
                                  showDivider: false,
                                  title: selectedDefinition.label,
                                  onClose: () => selectCheck(""),
                                })}
                              </GridPanel>
                            )}
                          </BlockStack>
                        );
                      }

                      const check = group[0];
                      const selected = selectedCheck === check.key;
                      return (
                        <CheckRow
                          key={check.key}
                          check={check}
                          total={scanned}
                          selected={selected}
                          onToggle={() => selectCheck(selected ? "" : check.key)}
                        >
                          {selected && selectedDefinition && renderPanel({})}
                        </CheckRow>
                      );
                    }
                  )}
                  {visibleChecks.length === 0 && (
                    <Text as="p" tone="subdued">
                      No checks in this view.
                    </Text>
                  )}
                </BlockStack>
              </BlockStack>
            )}
          </BlockStack>
        </Box>

        {/* Footer */}
        {audit?.completed_at && (
          <>
            <Divider />
            <Box padding="400" background="bg-surface-secondary">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <Text as="p" tone="subdued" variant="bodySm">
                  Last audit:{" "}
                  {new Date(audit.completed_at).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </Text>
                <Link url="https://help.shopify.com/en/manual/online-store/blogs" external>
                  Blog best practices
                </Link>
              </InlineStack>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
}